<template>
  <div class="screen">
    <component v-if="mode === 'multiple' && 'IconComponent' in currentStage" :is="currentStage.IconComponent" />
    <component v-else-if="mode === 'single' && 'IconComponent' in props" :is="props.IconComponent" />
    <slot />
    <template v-for="(stage, idx) in stages" :key="stage.sentence" v-if="!error && mode === 'multiple'">
      <template v-if="idx === currStageIndex">
        <span v-if="!error" class="focus">&nbsp;{{ typedText }}</span>
      </template>
      <template v-else>
        <span v-if="!error" class="inactive"
          >&nbsp;{{ stage.sentence }}&nbsp;<template v-if="idx < currStageIndex && stage.checked">✓</template></span
        >
      </template>
    </template>
    <template v-if="mode === 'single'">
      <span v-if="!error">&nbsp;{{ typedText }}</span>
    </template>
    <template v-else>
      <slot name="error" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { type Component, computed, onMounted, ref } from 'vue'
import { useTimeoutFn } from '@vueuse/core'

interface BaseProps {
  error?: string
  loop?: boolean
  speed?: number
}
export type SingleLineProps = BaseProps & {
  stages: {
    sentence: string
    checked: boolean
  }[]
  mode: 'single'
  IconComponent: Component
}
export type MultiLineProps = BaseProps & {
  stages: {
    IconComponent: Component
    sentence: string
    checked: boolean
  }[]
  mode: 'multiple'
}
type LoaderProps = SingleLineProps | MultiLineProps

const props = withDefaults(defineProps<LoaderProps>(), {
  type: 'elephant',
  loop: true,
  speed: 10,
})

const typedText = ref('')
const typedIndex = ref(0)
const currStageIndex = ref(0)

const graphemesArray = props.stages.map(stage => {
  const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' })
  return Array.from(segmenter.segment(stage.sentence + (stage.checked ? ' ✓' : '')), ({ segment }) => segment)
})

const currentStage = computed(() => props.stages[currStageIndex.value])

const handleTypeAnimation = () => {
  if (props.error) {
    return
  }

  const currentGraphemes = graphemesArray[currStageIndex.value]
  typedIndex.value++
  typedText.value = currentGraphemes.slice(0, typedIndex.value).join('')

  if (typedIndex.value !== currentGraphemes.length) {
    useTimeoutFn(handleTypeAnimation, props.speed)
    return
  }

  setTimeout(() => {
    typedIndex.value = 0

    if (currStageIndex.value + 1 < props.stages.length) {
      currStageIndex.value++
    } else {
      if (props.loop) {
        currStageIndex.value = 0
      } else {
        return
      }
    }

    useTimeoutFn(handleTypeAnimation, props.speed)
  }, 2000)
}

onMounted(() => {
  handleTypeAnimation()
})
</script>

<style lang="scss" scoped>
.screen {
  @include flex-column($spacing-4);
  position: fixed;
  inset: 0;
  background-color: $green-200;
  z-index: 3000;
  height: 100vh;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: $text-xl;
  color: white;

  @include mobile {
    font-size: $text-base;
    padding-inline: $spacing-6;
  }

  :deep(span) {
    min-height: 1.5rem;
    display: inline-flex;
    align-items: center; // Center text vertically within the span

    @media (max-width: 380px) {
      min-height: 2.3rem;
    }
  }

  .focus {
    font-weight: $font-medium;
  }

  .inactive {
    color: $grey-50;
    opacity: 0.5;
  }
}
</style>
